import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import PublicIcon from '@mui/icons-material/Public';

import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  ArrowForward,
  ArrowBack,
  CheckCircle,
} from '@mui/icons-material';

const steps = [
  'Site & Configuration',
  'Staff Roster',
  'Menu & Inventory',
  'Sales Data',
  'Utilities',
  'Waste Tracking',
  'Events & Exceptions',
  'Social Media'
];

const Onboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const { register, handleSubmit, formState: { errors }, watch, trigger } = useForm();
  const navigate = useNavigate();

  const [staffMembers, setStaffMembers] = useState([{ id: 1 }]);
  const [menuItems, setMenuItems] = useState([{ id: 1 }]);
  const [roles, setRoles] = useState([{ name: '', permissions: [] }]);

  const handleNext = async () => {
    setLoading(true);
    
    // Validate current step
    const stepFields = getStepFields(activeStep);
    const isValid = await trigger(stepFields);
    
    if (!isValid) {
      setLoading(false);
      return;
    }

    // Mark step as completed
    if (!completedSteps.includes(activeStep)) {
      setCompletedSteps([...completedSteps, activeStep]);
    }

    // Add slight delay for smooth transition
    setTimeout(() => {
      if (activeStep === steps.length - 1) {
        // Show completion animation before navigating
        setLoading(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        setActiveStep((prevStep) => prevStep + 1);
        setLoading(false);
        
        // Scroll to top when changing steps
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 500);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStepFields = (step) => {
    const fieldMap = {
      0: ['restaurantName', 'address', 'postcode', 'serviceType'],
      1: staffMembers.map((_, index) => [
        `staff.${index}.firstName`,
        `staff.${index}.lastName`,
        `staff.${index}.hireDate`
      ]).flat(),
      2: menuItems.map((_, index) => [
        `menu.${index}.name`,
        `menu.${index}.category`,
        `menu.${index}.price`
      ]).flat(),
      3: ['posFile'],
      4: ['electricityUsage', 'gasUsage', 'waterConsumption'],
      5: ['wasteTrackingMethod', 'wasteStreams'],
      6: ['eventName', 'eventDate'],
      7: ['facebookUsername', 'instagramUsername']
    };
    return fieldMap[step] || [];
  };

  const addStaffMember = () => {
    setStaffMembers([...staffMembers, { id: Date.now() }]);
  };

  const removeStaffMember = (id) => {
    if (staffMembers.length > 1) {
      setStaffMembers(staffMembers.filter(member => member.id !== id));
    }
  };

  const addMenuItem = () => {
    setMenuItems([...menuItems, { id: Date.now() }]);
  };

  const removeMenuItem = (id) => {
    if (menuItems.length > 1) {
      setMenuItems(menuItems.filter(item => item.id !== id));
    }
  };

  const addRole = () => {
    setRoles([...roles, { name: '', permissions: [] }]);
  };

  const removeRole = (index) => {
    if (roles.length > 1) {
      const newRoles = [...roles];
      newRoles.splice(index, 1);
      setRoles(newRoles);
    }
  };

  const permissionsOptions = [
    'View Sales',
    'Edit Roster',
    'Approve Stock',
    'Manage Menu',
    'View Reports',
    'Manage Staff'
  ];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
              🏢 Restaurant Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Tell us about your restaurant to personalize your sustainability dashboard
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Restaurant Name"
                  {...register('restaurantName', { required: 'Restaurant name is required' })}
                  error={!!errors.restaurantName}
                  helperText={errors.restaurantName?.message}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Postcode"
                  {...register('postcode', { required: 'Postcode is required' })}
                  error={!!errors.postcode}
                  helperText={errors.postcode?.message}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Full Address"
                  {...register('address', { required: 'Address is required' })}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Service Type</InputLabel>
                  <Select
                    label="Service Type"
                    {...register('serviceType', { required: 'Service type is required' })}
                    error={!!errors.serviceType}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="QSR">Quick Service Restaurant</MenuItem>
                    <MenuItem value="Fine Dining">Fine Dining</MenuItem>
                    <MenuItem value="Takeaway">Takeaway</MenuItem>
                    <MenuItem value="Cafe">Cafe</MenuItem>
                    <MenuItem value="Bar">Bar</MenuItem>
                    <MenuItem value="Food Truck">Food Truck</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

           <Box sx={{ mt: 4 }}>
  <Typography variant="h6" gutterBottom fontWeight={600}>
    ⏰ Operating Hours
  </Typography>

  <Grid container spacing={3}>
    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
      <Grid item xs={12} sm={6} md={4} key={day}>
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
            },
          }}
        >
          <Typography 
            variant="subtitle1" 
            gutterBottom 
            fontWeight={600} 
            sx={{ mb: 2, color: '#333' }}
          >
            {day}
          </Typography>

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="Open"
                type="time"
                InputLabelProps={{ shrink: true }}
                {...register(`${day.toLowerCase()}Open`)}
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    transition: '0.3s',
                    '&:hover fieldset': { borderColor: '#3b82f6' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="Close"
                type="time"
                InputLabelProps={{ shrink: true }}
                {...register(`${day.toLowerCase()}Close`)}
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    transition: '0.3s',
                    '&:hover fieldset': { borderColor: '#3b82f6' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <FormControlLabel
                control={<Checkbox {...register(`${day.toLowerCase()}Closed`)} />}
                label=""
                sx={{
                  m: 0,
                  '& .MuiCheckbox-root': {
                    color: '#3b82f6',
                    transition: '0.3s',
                    '&:hover': { color: '#1e40af' },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid>
    ))}
  </Grid>
</Box>


            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                👥 Role Configuration
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addRole}
                variant="outlined"
                sx={{ mb: 2, borderRadius: 2 }}
              >
                Add Role
              </Button>
              {roles.map((role, index) => (
                <Card key={index} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Role Name"
                        value={role.name}
                        onChange={(e) => {
                          const newRoles = [...roles];
                          newRoles[index].name = e.target.value;
                          setRoles(newRoles);
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Permissions</InputLabel>
                        <Select
                          multiple
                          value={role.permissions}
                          onChange={(e) => {
                            const newRoles = [...roles];
                            newRoles[index].permissions = e.target.value;
                            setRoles(newRoles);
                          }}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          {permissionsOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              <Checkbox checked={role.permissions.indexOf(option) > -1} />
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {roles.length > 1 && (
                      <Grid item xs={12} sm={1}>
                        <IconButton onClick={() => removeRole(index)} color="error" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              ))}
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
              👨‍🍳 Staff Roster
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Add your team members to track their environmental impact
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={addStaffMember}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Add Staff Member
              </Button>
            </Box>

            {staffMembers.map((member, index) => (
              <Card key={member.id} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      {...register(`staff.${index}.firstName`, { required: 'First name is required' })}
                      error={!!errors[`staff.${index}.firstName`]}
                      helperText={errors[`staff.${index}.firstName`]?.message}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      {...register(`staff.${index}.lastName`, { required: 'Last name is required' })}
                      error={!!errors[`staff.${index}.lastName`]}
                      helperText={errors[`staff.${index}.lastName`]?.message}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Hire Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      {...register(`staff.${index}.hireDate`)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Hourly Wage"
                      type="number"
                      {...register(`staff.${index}.hourlyWage`)}
                      size="small"
                      InputProps={{ startAdornment: '£' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Avg Commute (km)"
                      type="number"
                      {...register(`staff.${index}.avgCommute`)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Role</InputLabel>
                      <Select
                        label="Role"
                        {...register(`staff.${index}.role`)}
                      >
                        {roles.map((role, idx) => (
                          <MenuItem key={idx} value={role.name}>{role.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {staffMembers.length > 1 && (
                    <Grid item xs={12} textAlign="right">
                      <IconButton 
                        onClick={() => removeStaffMember(member.id)} 
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Card>
            ))}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
              🍽️ Menu & Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Add your menu items and track ingredient usage
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={addMenuItem}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Add Menu Item
              </Button>
            </Box>

            {menuItems.map((item, index) => (
              <Card key={item.id} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Item Name"
                      {...register(`menu.${index}.name`, { required: 'Item name is required' })}
                      error={!!errors[`menu.${index}.name`]}
                      helperText={errors[`menu.${index}.name`]?.message}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Category"
                      {...register(`menu.${index}.category`)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Selling Price"
                      type="number"
                      {...register(`menu.${index}.price`)}
                      size="small"
                      InputProps={{ startAdornment: '£' }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="600">
                      Ingredients (per serving)
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { label: 'Meat (kg)', field: 'meat' },
                        { label: 'Vegetable (kg)', field: 'vegetable' },
                        { label: 'Dairy (ltrs)', field: 'dairy' },
                        { label: 'Eggs (units)', field: 'eggs' },
                        { label: 'Spices (kg)', field: 'spices' },
                        { label: 'Packaging (kg)', field: 'packaging' },
                      ].map((ingredient) => (
                        <Grid item xs={6} sm={4} md={2} key={ingredient.field}>
                          <TextField
                            fullWidth
                            label={ingredient.label}
                            type="number"
                            {...register(`menu.${index}.${ingredient.field}`)}
                            size="small"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Average Daily Usage (kg)"
                      type="number"
                      {...register(`menu.${index}.dailyUsage`)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Current Stock (kg)"
                      type="number"
                      {...register(`menu.${index}.currentStock`)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Supplier Name"
                      {...register(`menu.${index}.supplier`)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Restock Lead Time (days)"
                      type="number"
                      {...register(`menu.${index}.leadTime`)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Recipe/Spec"
                      {...register(`menu.${index}.recipe`)}
                      size="small"
                    />
                  </Grid>
                  {menuItems.length > 1 && (
                    <Grid item xs={12} textAlign="right">
                      <IconButton 
                        onClick={() => removeMenuItem(item.id)} 
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Card>
            ))}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease', textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
              📊 Sales Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Upload your POS data to analyze sales patterns and calculate accurate carbon footprint
            </Typography>

            <Card sx={{ p: 4, borderRadius: 3, maxWidth: 500, mx: 'auto' }}>
              <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Upload POS CSV File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Supported formats: .csv (Last 12 months data recommended)
              </Typography>
              
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                Select File
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  {...register('posFile')}
                />
              </Button>
              
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                This data helps us calculate your carbon emissions from food production accurately
              </Alert>
            </Card>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
              ⚡ Utilities (Energy & Water)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Track your utility consumption for accurate sustainability metrics
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Has Smart Meter?</InputLabel>
                  <Select
                    label="Has Smart Meter?"
                    {...register('hasSmartMeter')}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Electricity Usage (kWh/month)"
                  type="number"
                  {...register('electricityUsage')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Gas Usage (kWh/month)"
                  type="number"
                  {...register('gasUsage')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Water Consumption (m³/month)"
                  type="number"
                  {...register('waterConsumption')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  🚗 Scope 1 Emissions
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Fuel Type</InputLabel>
                  <Select
                    label="Fuel Type"
                    {...register('fuelType')}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="diesel">Diesel</MenuItem>
                    <MenuItem value="lpg">LPG</MenuItem>
                    <MenuItem value="refrigerant">Refrigerant</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fuel Volume (Liters or kg/month)"
                  type="number"
                  {...register('fuelVolume')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  Upload Utility Bills
                  <input type="file" hidden multiple {...register('utilityBills')} />
                </Button>
              </Grid>
            </Grid>
          </Box>
        );

     case 5:
  return (
    <Box sx={{ animation: 'slideIn 0.5s ease' }}>
      <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
        🗑️ Waste Tracking
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Configure how you track waste generation
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel 
              id="tracking-method-label"
              sx={{
                backgroundColor: 'white',
                px: 1,
                '&.Mui-focused': {
                  color: 'primary.main',
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '&.MuiFormLabel-filled': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                }
              }}
            >
              Tracking Method
            </InputLabel>
            <Select
              labelId="tracking-method-label"
              label="Tracking Method"
              defaultValue=""
              {...register('wasteTrackingMethod')}
              sx={{ 
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                }
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select tracking method</em>
              </MenuItem>
              <MenuItem value="weight">Weight</MenuItem>
              <MenuItem value="binCount">Bin Count</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {watch('wasteTrackingMethod') === 'binCount' && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Bin Size (Liters)"
              type="number"
              {...register('binSize')}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  }
                }
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel 
              id="waste-streams-label"
              sx={{
                backgroundColor: 'white',
                px: 1,
                '&.Mui-focused': {
                  color: 'primary.main',
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '&.MuiFormLabel-filled': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                }
              }}
            >
              Waste Streams
            </InputLabel>
            <Select
              labelId="waste-streams-label"
              multiple
              label="Waste Streams"
              defaultValue={[]}
              {...register('wasteStreams')}
              sx={{ 
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                }
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      Select waste streams
                    </Typography>
                  ) : (
                    selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))
                  )}
                </Box>
              )}
            >
              {['General Waste', 'Food Waste', 'Recycling', 'Glass', 'Plastic', 'Paper'].map((stream) => (
                <MenuItem key={stream} value={stream.toLowerCase()}>
                  <Checkbox checked={watch('wasteStreams')?.includes(stream.toLowerCase())} />
                  {stream}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
      case 6:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
              📅 Events & Exceptions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Add local events that might affect your operations
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Event Name"
                  {...register('eventName')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Event Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  {...register('eventDate')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Event Frequency</InputLabel>
                  <Select
                    label="Event Frequency"
                    {...register('eventFrequency')}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="once">Once</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 7:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
           <Typography variant="h5" fontWeight={600} gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
  <PublicIcon  fontSize="large" /> Social Media
</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Connect your social accounts for marketing insights
            </Typography>

            <Grid container spacing={3}>
              {['Facebook', 'Instagram', 'TikTok', 'WhatsApp'].map((platform) => (
                <Grid item xs={12} key={platform}>
                  <Card sx={{ p: 3, borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle1" fontWeight="600">{platform}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Username/Email"
                          {...register(`${platform.toLowerCase()}Username`)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Password"
                          type="password"
                          {...register(`${platform.toLowerCase()}Password`)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <FormControlLabel
                          control={<Checkbox {...register(`${platform.toLowerCase()}Connected`)} />}
                          label=""
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const onSubmit = async () => {
    await handleNext();
  };

  return (
    <>
      {/* Loading Backdrop */}
      <Backdrop
        open={loading && activeStep === steps.length - 1}
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(16, 185, 129, 0.95)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 80, mb: 3, animation: 'scaleIn 0.5s ease' }} />
          <Typography variant="h4" gutterBottom fontWeight="bold">
            🎉 Setup Complete!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 400 }}>
            Your sustainability dashboard is being configured...
          </Typography>
          <CircularProgress color="inherit" />
        </Box>
      </Backdrop>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, md: 4 }, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            border: '1px solid',
            borderColor: 'grey.200',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              gutterBottom 
              fontWeight="800"
              sx={{
                background: 'linear-gradient(45deg, #5f6362ff 30%, #3b82f6 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Complete Your Profile
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Step {activeStep + 1} of {steps.length} • {steps[activeStep]}
            </Typography>
            
            {/* Progress Bar */}
            <Box sx={{ width: '100%', mb: 3 }}>
              <Box sx={{ 
                width: '100%', 
                height: 6, 
                backgroundColor: 'grey.200',
                borderRadius: 3,
                overflow: 'hidden'
              }}>
                <Box sx={{
                  width: `${((activeStep + 1) / steps.length) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #10b981, #3b82f6)',
                  borderRadius: 3,
                  transition: 'width 0.5s ease',
                }} />
              </Box>
            </Box>
          </Box>

          {/* Stepper */}
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel 
            sx={{ 
              mb: 5,
              '& .MuiStepLabel-root': {
                '& .MuiStepLabel-label': {
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                }
              }
            }}
          >
            {steps.map((label, index) => (
              <Step key={label} completed={completedSteps.includes(index)}>
                <StepLabel 
                  sx={{
                    '& .MuiStepIcon-root': {
                      '&.Mui-completed': {
                        color: '#10b981',
                      },
                      '&.Mui-active': {
                        color: '#3b82f6',
                      },
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Form Content */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ 
              mb: 4,
              minHeight: 400,
              animation: 'fadeIn 0.3s ease'
            }}>
              {renderStepContent(activeStep)}
            </Box>

            {/* Navigation Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Button
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
                variant="outlined"
                startIcon={<ArrowBack />}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  display: activeStep === 0 ? 'none' : 'flex'
                }}
              >
                Back
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                endIcon={activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 4,
                  py: 1.2,
                  background: 'linear-gradient(45deg, #10b981 30%, #3b82f6 90%)',
                  fontWeight: 600,
                  ml: 'auto'
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  'Complete Setup'
                ) : (
                  'Next Step'
                )}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>

      {/* Keyframe Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideIn {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          @keyframes scaleIn {
            0% { transform: scale(0); opacity: 0; }
            70% { transform: scale(1.2); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </>
  );
};

export default Onboarding;